import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  DevicesOther as DevicesIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Description as DocumentIcon,
  Check as AcceptIcon,
  Check as CheckIcon,
  Close as RejectIcon,
  Download as DownloadIcon,
  Payment as PaymentIcon,
  Folder as FolderIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  PictureAsPdf as PdfIcon,
  Settings as SettingsIcon,
  VpnKey as PasswordIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { authAPI, documentsAPI, documentTypesAPI, clientDocumentsAPI, clientInformationAPI, settingsAPI } from '../services/api';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ClientPortalPage = () => {
  const [portalData, setPortalData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [devices, setDevices] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [clientDocs, setClientDocs] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [clientInfo, setClientInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Visibility settings
  const [visibilitySettings, setVisibilitySettings] = useState({
    hide_devices: false,
    hide_active_devices: false,
    hide_vehicles: false,
  });
  
  // Client documents dialog
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [docFormData, setDocFormData] = useState({ document_type: '', name: '', notes: '' });
  const [docSubmitting, setDocSubmitting] = useState(false);
  
  // Settings dialog
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Client info editing
  const [infoEditing, setInfoEditing] = useState(false);
  const [infoSaving, setInfoSaving] = useState(false);
  
  // Profile editing
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileData, setProfileData] = useState({});
  const [profileSaving, setProfileSaving] = useState(false);
  
  // Reject dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingDoc, setRejectingDoc] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  
  // Accept dialog state
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [acceptingDoc, setAcceptingDoc] = useState(null);

  // Helper function to get correct tab index considering hidden tabs
  const getTabIndex = (originalIndex) => {
    if (visibilitySettings.hide_devices && originalIndex >= 3) {
      return originalIndex - 1; // Shift left when devices tab is hidden
    }
    return originalIndex;
  };

  useEffect(() => {
    fetchPortalData();
  }, []);

  const fetchPortalData = async () => {
    try {
      setLoading(true);
      // Fetch visibility settings first
      try {
        const visibilityRes = await settingsAPI.getVisibilitySettings();
        setVisibilitySettings(visibilityRes.data);
      } catch (e) {
        // If visibility settings fail, use defaults
        console.warn('Could not fetch visibility settings, using defaults');
      }
      
      // Fetch core data first
      const [portalRes, invoicesRes, devicesRes, documentsRes, paymentsRes] = await Promise.all([
        authAPI.getClientPortal(),
        authAPI.getClientInvoices(),
        authAPI.getClientDevices(),
        authAPI.getClientDocuments(),
        authAPI.getClientPayments(),
      ]);
      setPortalData(portalRes.data);
      setInvoices(invoicesRes.data);
      setDevices(devicesRes.data);
      setDocuments(documentsRes.data);
      setPayments(paymentsRes.data);
      
      // Fetch optional data (don't fail if these don't work)
      try {
        const clientDocsRes = await clientDocumentsAPI.getMyDocuments();
        setClientDocs(clientDocsRes.data || []);
      } catch (e) { setClientDocs([]); }
      
      try {
        const docTypesRes = await documentTypesAPI.getActive();
        setDocumentTypes(docTypesRes.data || []);
      } catch (e) { setDocumentTypes([]); }
      
      try {
        const clientInfoRes = await clientInformationAPI.getMyInformation();
        setClientInfo(clientInfoRes.data || []);
      } catch (e) { setClientInfo([]); }
      
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Document handlers
  const handleOpenDocDialog = (doc = null) => {
    if (doc) {
      setEditingDoc(doc);
      setDocFormData({ document_type: doc.document_type, name: doc.name, notes: doc.notes || '' });
    } else {
      setEditingDoc(null);
      setDocFormData({ document_type: '', name: '', notes: '' });
    }
    setSelectedFile(null);
    setDocDialogOpen(true);
  };
  
  const handleCloseDocDialog = () => {
    setDocDialogOpen(false);
    setEditingDoc(null);
    setSelectedFile(null);
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.toLowerCase().endsWith('.pdf') && file.size <= 10 * 1024 * 1024) {
      setSelectedFile(file);
      if (!docFormData.name) setDocFormData({ ...docFormData, name: file.name.replace('.pdf', '') });
    } else {
      alert('Fichier PDF requis (max 10 Mo)');
    }
  };
  
  const handleSubmitDoc = async () => {
    if (!docFormData.document_type || !docFormData.name.trim()) return alert('Champs obligatoires manquants');
    if (!editingDoc && !selectedFile) return alert('Fichier PDF requis');
    try {
      setDocSubmitting(true);
      const formData = new FormData();
      formData.append('document_type', docFormData.document_type);
      formData.append('name', docFormData.name);
      formData.append('notes', docFormData.notes);
      if (selectedFile) formData.append('file', selectedFile);
      if (editingDoc) await clientDocumentsAPI.update(editingDoc.id, formData);
      else await clientDocumentsAPI.create(formData);
      handleCloseDocDialog();
      setSuccessMessage(editingDoc ? 'Document modifié' : 'Document ajouté');
      fetchPortalData();
    } catch (err) {
      alert(err.response?.data?.file?.[0] || 'Erreur');
    } finally {
      setDocSubmitting(false);
    }
  };
  
  const handleDeleteDoc = async (id) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await clientDocumentsAPI.delete(id);
      setSuccessMessage('Document supprimé');
      fetchPortalData();
    } catch (err) {
      alert('Erreur');
    }
  };
  
  // Info handlers
  const handleInfoChange = (index, value) => {
    const updated = [...clientInfo];
    updated[index] = { ...updated[index], value };
    setClientInfo(updated);
  };
  
  const handleSaveInfo = async () => {
    try {
      setInfoSaving(true);
      await clientInformationAPI.bulkUpdate({
        information: clientInfo.map(i => ({ information_type: i.information_type, value: i.value || '' }))
      });
      setSuccessMessage('Informations enregistrées');
      setInfoEditing(false);
    } catch (err) {
      alert('Erreur');
    } finally {
      setInfoSaving(false);
    }
  };
  
  // Password handler
  const handleChangePassword = async () => {
    if (!passwordData.current_password) return alert('Veuillez entrer le mot de passe actuel');
    if (!passwordData.new_password) return alert('Veuillez entrer le nouveau mot de passe');
    if (!passwordData.confirm_password) return alert('Veuillez confirmer le nouveau mot de passe');
    if (passwordData.new_password !== passwordData.confirm_password) return alert('Mots de passe différents');
    if (passwordData.new_password.length < 8) return alert('Minimum 8 caractères');
    try {
      setPasswordChanging(true);
      await authAPI.changePassword({ 
        old_password: passwordData.current_password, 
        new_password: passwordData.new_password,
        new_password_confirm: passwordData.confirm_password 
      });
      setSuccessMessage('Mot de passe modifié');
      setSettingsDialogOpen(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      const errorData = err.response?.data;
      let errorMessage = 'Erreur';
      
      if (errorData) {
        // Show specific validation errors
        if (errorData.old_password) {
          errorMessage = errorData.old_password[0];
        } else if (errorData.new_password) {
          errorMessage = errorData.new_password[0];
        } else if (errorData.new_password_confirm) {
          errorMessage = errorData.new_password_confirm[0];
        } else if (errorData.non_field_errors) {
          errorMessage = errorData.non_field_errors[0];
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        }
      }
      
      alert(errorMessage);
    } finally {
      setPasswordChanging(false);
    }
  };
  
  // Profile handlers
  const startProfileEdit = () => {
    setProfileData({
      rc_number: portalData?.rc_number || '',
      nif_number: portalData?.nif_number || '',
      nis_number: portalData?.nis_number || '',
      ai_number: portalData?.ai_number || '',
    });
    setProfileEditing(true);
  };
  
  const handleSaveProfile = async () => {
    try {
      setProfileSaving(true);
      await authAPI.updateClientProfile(profileData);
      setSuccessMessage('Profil mis à jour');
      setProfileEditing(false);
      fetchPortalData();
    } catch (err) {
      alert('Erreur lors de la mise à jour');
    } finally {
      setProfileSaving(false);
    }
  };

  const openAcceptDialog = (doc) => {
    setAcceptingDoc(doc);
    setAcceptDialogOpen(true);
  };

  const handleAcceptDocument = async () => {
    try {
      setActionLoading({ ...actionLoading, [acceptingDoc.id]: true });
      const response = await authAPI.acceptDocument(acceptingDoc.id);
      
      // Show message with invoice info if created
      const message = response.data?.message || 'Document accepté avec succès!';
      alert(message);
      
      setAcceptDialogOpen(false);
      setAcceptingDoc(null);
      fetchPortalData();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'acceptation');
    } finally {
      setActionLoading({ ...actionLoading, [acceptingDoc?.id]: false });
    }
  };

  const openRejectDialog = (doc) => {
    setRejectingDoc(doc);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectDocument = async () => {
    if (!rejectReason.trim()) {
      alert('Veuillez fournir une raison pour le rejet');
      return;
    }
    
    try {
      setActionLoading({ ...actionLoading, [rejectingDoc.id]: true });
      await authAPI.rejectDocument(rejectingDoc.id, rejectReason);
      alert('Document rejeté');
      setRejectDialogOpen(false);
      setRejectingDoc(null);
      setRejectReason('');
      fetchPortalData();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors du rejet');
    } finally {
      setActionLoading({ ...actionLoading, [rejectingDoc?.id]: false });
    }
  };

  const handleViewDocument = async (doc) => {
    // Mark as viewed if not already
    if (doc.is_new) {
      try {
        await authAPI.markDocumentViewed(doc.id);
        // Update local state to remove NEW label
        setDocuments(prev => prev.map(d => 
          d.id === doc.id ? { ...d, is_new: false, client_viewed: true } : d
        ));
      } catch (err) {
        console.error('Error marking document as viewed:', err);
      }
    }
    
    // Download PDF securely via API (not direct URL)
    if (doc.pdf_file) {
      try {
        const response = await documentsAPI.download(doc.id);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        // Cleanup after a delay
        setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      } catch (err) {
        console.error('Error downloading PDF:', err);
        alert('Erreur lors du téléchargement du PDF');
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_client': return 'info';
      case 'client_accepted': return 'success';
      case 'validated': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending_client': return 'En attente de votre réponse';
      case 'client_accepted': return 'Accepté';
      case 'validated': return 'Validé';
      case 'rejected': return 'Rejeté';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  // Count pending and new documents
  const pendingDocs = documents.filter(d => d.status === 'pending_client').length;
  const newDocs = documents.filter(d => d.is_new).length;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Portail Client
      </Typography>

      {/* New Documents Alert */}
      {newDocs > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Vous avez <strong>{newDocs}</strong> nouveau(x) document(s) à consulter.
          {pendingDocs > 0 && ` Dont ${pendingDocs} en attente de votre réponse.`}
        </Alert>
      )}

      {/* Company Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              {portalData?.company_name}
            </Typography>
            <Chip 
              label={portalData?.client_code} 
              size="small" 
              sx={{ ml: 2 }} 
              color="primary"
            />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Contact:</strong> {portalData?.contact_person || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Email:</strong> {portalData?.email || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Téléphone:</strong> {portalData?.phone || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Adresse:</strong> {portalData?.address || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Ville:</strong> {portalData?.city || '-'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {!visibilitySettings.hide_active_devices && (
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="primary">
                  {portalData?.total_active_devices || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Dispositifs Actifs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        {!visibilitySettings.hide_vehicles && (
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="primary">
                  {portalData?.total_vehicles || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Véhicules
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color={newDocs > 0 ? 'error.main' : 'primary'}>
                {newDocs}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Nouveaux documents
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab icon={<DocumentIcon />} label={`Documents ${newDocs > 0 ? `(${newDocs})` : ''}`} />
          <Tab icon={<ReceiptIcon />} label="Factures" />
          <Tab icon={<PaymentIcon />} label="Paiements" />
          {!visibilitySettings.hide_devices && <Tab icon={<DevicesIcon />} label="Dispositifs" />}
          <Tab icon={<FolderIcon />} label="Mes Fichiers" />
          <Tab icon={<InfoIcon />} label="Mes Infos" />
          <Tab icon={<SettingsIcon />} label="Paramètres" />
        </Tabs>
        <Divider />

        {/* Documents Tab */}
        <TabPanel value={tabValue} index={getTabIndex(0)}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>N° Document</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Total TTC</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Aucun document disponible
                    </TableCell>
                  </TableRow>
                ) : (
                  documents.map((doc) => (
                    <TableRow key={doc.id} sx={{ 
                      '&:hover': { bgcolor: 'action.hover' }
                    }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <strong>{doc.document_number}</strong>
                          {doc.is_new && (
                            <Chip 
                              label="NOUVEAU" 
                              size="small" 
                              color="error"
                              sx={{ 
                                fontSize: '0.65rem', 
                                height: 20,
                                animation: 'pulse 1.5s infinite',
                                '@keyframes pulse': {
                                  '0%': { opacity: 1 },
                                  '50%': { opacity: 0.6 },
                                  '100%': { opacity: 1 },
                                }
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={doc.document_type_display} 
                          size="small"
                          color={doc.document_type === 'proforma' ? 'warning' : 'primary'}
                        />
                      </TableCell>
                      <TableCell>{doc.document_date}</TableCell>
                      <TableCell align="right">
                        <strong>
                          {doc.total_ttc ? `${parseFloat(doc.total_ttc).toLocaleString('fr-FR')} DZD` : '-'}
                        </strong>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getStatusLabel(doc.status)} 
                          color={getStatusColor(doc.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          {doc.pdf_file ? (
                            <Tooltip title="Voir / Télécharger PDF">
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleViewDocument(doc)}
                              >
                                Voir PDF
                              </Button>
                            </Tooltip>
                          ) : (
                            <Chip label="PDF non disponible" size="small" color="default" />
                          )}
                          
                          {/* PDF access status indicator */}
                          {doc.pdf_file && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {(doc.pdf_downloaded || doc.client_viewed) ? (
                                <Chip 
                                  label="PDF consulté" 
                                  size="small" 
                                  color="success" 
                                  variant="outlined"
                                  icon={<CheckIcon />}
                                />
                              ) : (
                                <Chip 
                                  label="PDF non consulté" 
                                  size="small" 
                                  color="warning" 
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          )}
                          
                          {/* Accept/Reject buttons for pending proformas */}
                          {doc.status === 'pending_client' && doc.document_type === 'proforma' && (
                            <>
                              <Tooltip title={(doc.pdf_downloaded || doc.client_viewed) ? "Accepter la facture proforma" : "Veuillez d'abord consulter le PDF"}>
                                <span>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    startIcon={actionLoading[doc.id] ? <CircularProgress size={16} /> : <AcceptIcon />}
                                    onClick={() => openAcceptDialog(doc)}
                                    disabled={actionLoading[doc.id] || !(doc.pdf_downloaded || doc.client_viewed)}
                                  >
                                    Accepter
                                  </Button>
                                </span>
                              </Tooltip>
                              <Tooltip title={(doc.pdf_downloaded || doc.client_viewed) ? "Refuser la facture proforma" : "Veuillez d'abord consulter le PDF"}>
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<RejectIcon />}
                                    onClick={() => openRejectDialog(doc)}
                                    disabled={actionLoading[doc.id] || !(doc.pdf_downloaded || doc.client_viewed)}
                                  >
                                    Refuser
                                  </Button>
                                </span>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Invoices Tab */}
        <TabPanel value={tabValue} index={getTabIndex(1)}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>N° Facture</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Période</TableCell>
                  <TableCell align="right">Total HT</TableCell>
                  <TableCell align="right">TVA</TableCell>
                  <TableCell align="right">Total TTC</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Aucune facture disponible
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell>{invoice.document_number}</TableCell>
                      <TableCell>{invoice.document_date}</TableCell>
                      <TableCell>
                        {invoice.period_start && invoice.period_end
                          ? `${invoice.period_start} - ${invoice.period_end}`
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {invoice.total_ht ? `${parseFloat(invoice.total_ht).toLocaleString()} DZD` : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {invoice.total_vat ? `${parseFloat(invoice.total_vat).toLocaleString()} DZD` : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {invoice.total_ttc ? `${parseFloat(invoice.total_ttc).toLocaleString()} DZD` : '-'}
                        </strong>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Payments Tab */}
        <TabPanel value={tabValue} index={getTabIndex(2)}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>N° Facture</TableCell>
                  <TableCell align="right">Montant</TableCell>
                  <TableCell>Mode de paiement</TableCell>
                  <TableCell>Référence</TableCell>
                  <TableCell>Statut</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Aucun paiement enregistré
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>{payment.payment_date}</TableCell>
                      <TableCell>{payment.invoice_number}</TableCell>
                      <TableCell align="right">
                        <strong>
                          {parseFloat(payment.amount).toLocaleString('fr-FR')} DZD
                        </strong>
                      </TableCell>
                      <TableCell>{payment.payment_method_display}</TableCell>
                      <TableCell>{payment.reference || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={payment.status_display}
                          color={
                            payment.status === 'confirmed' ? 'success' :
                            payment.status === 'cancelled' ? 'error' : 'warning'
                          }
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Devices Tab */}
        {!visibilitySettings.hide_devices && (
          <TabPanel value={tabValue} index={getTabIndex(3)}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Mes Dispositifs
                {devices.length > 0 && (
                  <Chip 
                    label={`${devices.length} dispositif${devices.length > 1 ? 's' : ''}`} 
                    size="small" 
                    color="primary"
                    sx={{ ml: 2 }}
                  />
                )}
              </Typography>
            </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={60}>#</TableCell>
                  <TableCell>Véhicule</TableCell>
                  <TableCell>Immatriculation</TableCell>
                  <TableCell>Date Installation</TableCell>
                  <TableCell>IMEI Dispositif</TableCell>
                  <TableCell>Modèle</TableCell>
                  <TableCell>Statut</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Aucun dispositif disponible
                    </TableCell>
                  </TableRow>
                ) : (
                  devices.map((item, index) => (
                    <TableRow key={item.vehicle_id}>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {index + 1}
                      </TableCell>
                      <TableCell>{item.vehicle_name}</TableCell>
                      <TableCell>{item.registration_number || '-'}</TableCell>
                      <TableCell>{item.installation_date || '-'}</TableCell>
                      <TableCell>{item.device?.imei || '-'}</TableCell>
                      <TableCell>{item.device?.model || '-'}</TableCell>
                      <TableCell>
                        {item.device ? (
                          <Chip
                            icon={item.device.is_active ? <ActiveIcon /> : <InactiveIcon />}
                            label={item.device.is_active ? 'Actif' : 'Inactif'}
                            color={item.device.is_active ? 'success' : 'error'}
                            size="small"
                          />
                        ) : (
                          <Chip label="Non installé" size="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </TabPanel>
        )}

        {/* My Files Tab */}
        <TabPanel value={tabValue} index={getTabIndex(4)}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDocDialog()}>
              Ajouter un document
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nom</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clientDocs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">Aucun fichier</TableCell>
                  </TableRow>
                ) : (
                  clientDocs.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PdfIcon color="error" fontSize="small" />
                          <strong>{doc.name}</strong>
                        </Box>
                      </TableCell>
                      <TableCell><Chip label={doc.document_type_name} size="small" variant="outlined" /></TableCell>
                      <TableCell>{doc.created_at ? new Date(doc.created_at).toLocaleDateString('fr-FR') : '-'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {doc.file_url && (
                            <Tooltip title="Télécharger"><IconButton size="small" onClick={() => window.open(doc.file_url, '_blank')}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
                          )}
                          <Tooltip title="Modifier"><IconButton size="small" onClick={() => handleOpenDocDialog(doc)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Supprimer"><IconButton size="small" color="error" onClick={() => handleDeleteDoc(doc.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* My Info Tab */}
        <TabPanel value={tabValue} index={getTabIndex(5)}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Mes Informations</Typography>
            {!infoEditing ? (
              <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setInfoEditing(true)}>Modifier</Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" onClick={() => { setInfoEditing(false); fetchPortalData(); }} disabled={infoSaving}>Annuler</Button>
                <Button variant="contained" onClick={handleSaveInfo} disabled={infoSaving}>{infoSaving ? 'Enregistrement...' : 'Enregistrer'}</Button>
              </Box>
            )}
          </Box>
          {clientInfo.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>Aucune information à remplir</Typography>
          ) : (
            <Grid container spacing={2}>
              {clientInfo.map((info, index) => (
                <Grid item xs={12} md={6} key={info.information_type}>
                  <TextField
                    fullWidth
                    label={info.information_type_name}
                    value={info.value || ''}
                    onChange={(e) => handleInfoChange(index, e.target.value)}
                    disabled={!infoEditing}
                    required={info.is_required}
                    helperText={info.description}
                    type={info.field_type === 'number' ? 'number' : info.field_type === 'email' ? 'email' : info.field_type === 'date' ? 'date' : 'text'}
                    multiline={info.field_type === 'textarea'}
                    rows={info.field_type === 'textarea' ? 3 : 1}
                    inputProps={{ maxLength: info.max_length }}
                    InputLabelProps={info.field_type === 'date' ? { shrink: true } : undefined}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        {/* Settings Tab */}
        <TabPanel value={tabValue} index={getTabIndex(6)}>
          <Typography variant="h6" gutterBottom>Paramètres du compte</Typography>
          
          {/* Profile Info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>Informations fiscales</Typography>
                {!profileEditing ? (
                  <Button size="small" startIcon={<EditIcon />} onClick={startProfileEdit}>Modifier</Button>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" onClick={() => setProfileEditing(false)} disabled={profileSaving}>Annuler</Button>
                    <Button size="small" variant="contained" onClick={handleSaveProfile} disabled={profileSaving}>
                      {profileSaving ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                  </Box>
                )}
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="RC N°"
                    value={profileEditing ? profileData.rc_number : (portalData?.rc_number || '')}
                    onChange={(e) => setProfileData({ ...profileData, rc_number: e.target.value })}
                    disabled={!profileEditing}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="NIF N°"
                    value={profileEditing ? profileData.nif_number : (portalData?.nif_number || '')}
                    onChange={(e) => setProfileData({ ...profileData, nif_number: e.target.value })}
                    disabled={!profileEditing}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="NIS N°"
                    value={profileEditing ? profileData.nis_number : (portalData?.nis_number || '')}
                    onChange={(e) => setProfileData({ ...profileData, nis_number: e.target.value })}
                    disabled={!profileEditing}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="AI N°"
                    value={profileEditing ? profileData.ai_number : (portalData?.ai_number || '')}
                    onChange={(e) => setProfileData({ ...profileData, ai_number: e.target.value })}
                    disabled={!profileEditing}
                    size="small"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          
          {/* Password Change */}
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Changer le mot de passe</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Mot de passe actuel"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Nouveau mot de passe"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    size="small"
                    helperText="Minimum 8 caractères"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Confirmer"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    size="small"
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" onClick={handleChangePassword} disabled={passwordChanging}>
                  {passwordChanging ? 'Modification...' : 'Changer le mot de passe'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>
      </Paper>

      {/* Document Dialog */}
      <Dialog open={docDialogOpen} onClose={handleCloseDocDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDoc ? 'Modifier le document' : 'Ajouter un document'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              label="Type de document *"
              value={docFormData.document_type}
              onChange={(e) => setDocFormData({ ...docFormData, document_type: e.target.value })}
              fullWidth
              SelectProps={{ native: true }}
            >
              <option value="">-- Sélectionner --</option>
              {documentTypes.map((type) => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </TextField>
            <TextField
              label="Nom du document *"
              value={docFormData.name}
              onChange={(e) => setDocFormData({ ...docFormData, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Notes"
              value={docFormData.notes}
              onChange={(e) => setDocFormData({ ...docFormData, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <Box>
              <input accept="application/pdf" style={{ display: 'none' }} id="file-upload" type="file" onChange={handleFileChange} />
              <label htmlFor="file-upload">
                <Button variant="outlined" component="span" startIcon={<UploadIcon />} fullWidth>
                  {selectedFile ? selectedFile.name : (editingDoc ? 'Changer le fichier' : 'Sélectionner un PDF *')}
                </Button>
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDocDialog} disabled={docSubmitting}>Annuler</Button>
          <Button variant="contained" onClick={handleSubmitDoc} disabled={docSubmitting}>
            {docSubmitting ? 'Enregistrement...' : (editingDoc ? 'Modifier' : 'Ajouter')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Accept Confirmation Dialog */}
      <Dialog open={acceptDialogOpen} onClose={() => setAcceptDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmer l'acceptation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Document: <strong>{acceptingDoc?.document_number}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Êtes-vous sûr de vouloir accepter cette facture proforma ? 
            Cette action convertira automatiquement la proforma en facture définitive.
          </Typography>
          {!(acceptingDoc?.pdf_downloaded || acceptingDoc?.client_viewed) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Attention:</strong> Vous devez télécharger ou consulter le PDF au moins une fois avant de pouvoir accepter.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcceptDialogOpen(false)}>Annuler</Button>
          <Button 
            variant="contained" 
            color="success" 
            onClick={handleAcceptDocument}
            disabled={!(acceptingDoc?.pdf_downloaded || acceptingDoc?.client_viewed) || actionLoading[acceptingDoc?.id]}
          >
            {actionLoading[acceptingDoc?.id] ? 'Acceptation...' : 'Confirmer l\'acceptation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Refuser le document</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Document: <strong>{rejectingDoc?.document_number}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Êtes-vous sûr de vouloir refuser cette facture proforma ? 
            Cette action ne pourra pas être annulée.
          </Typography>
          {!(rejectingDoc?.pdf_downloaded || rejectingDoc?.client_viewed) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Attention:</strong> Vous devez télécharger ou consulter le PDF au moins une fois avant de pouvoir refuser.
              </Typography>
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Raison du refus"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Veuillez expliquer pourquoi vous refusez ce document..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Annuler</Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleRejectDocument}
            disabled={!rejectReason.trim() || !(rejectingDoc?.pdf_downloaded || rejectingDoc?.client_viewed) || actionLoading[rejectingDoc?.id]}
          >
            {actionLoading[rejectingDoc?.id] ? 'Refus...' : 'Confirmer le refus'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Message */}
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999 }}
          onClose={() => setSuccessMessage('')}
        >
          {successMessage}
        </Alert>
      )}
    </Box>
  );
};

export default ClientPortalPage;
